import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ConfigProvider } from 'antd';
import apiClient from '@skygroup/shared/API/apiClient';
import mergeDeep from '@skygroup/shared/utils/mergeConfigs';
import { ApiStatus } from '@skygroup/shared/components/crud-helpers/ModelApiMessage';
import { updateConfig } from '../store/config';
import { updateSearchInitialState } from '../modules/SearchDatePicker/searchDatePickerSlice';
import endpoints from '../utils/endpoints';
import getImagePathFromName from '@skygroup/shared/utils/getImagePathFromName';

// Import all app configs
import appStaticConf from '../appStaticConf';
import appConfigSky360 from '../appConfigSky360';
import appConfigSky360v2 from '../appConfigSky360v2';
import appConfigFamily from '../appConfigFamilybreak';
import appConfigHotel360 from '../appConfigHotel360';
import appConfigIssta from '../appConfigIssta';
import appConfigSap from '../appConfigSap';
import appConfigIpc from '../appConfigIpc';
import appConfigUniversal from '../appConfigUniversal';
import appConfigStudents from '../appConfigSkystudents';
import appConfigSoho from '../appConfigSoho';
import appConfigGuestRoom from '../appConfigGuestRoom';

const getDeparture = (value: any): string => {
  if (!value) return "ALP";

  // Case 1: Array
  if (Array.isArray(value)) {
    return value[0] || "ALP";
  }

  // Case 2: String like "ALP,TBS"
  if (typeof value === "string") {
    const parts = value.split(",").map(v => v.trim());
    return parts[0] || "ALP";
  }

  // Fallback
  return "ALP";
};

const setFavicon = (faviconPath: string): void => {
  try {
    let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    if (favicon) {
      favicon.href = getImagePathFromName(faviconPath);
    } else {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.href = getImagePathFromName(faviconPath);
      document.head.appendChild(newFavicon);
    }
  } catch (error) {
    console.error('Error setting favicon:', error);
  }
};

export default function useAppConfig() {
  const dispatch = useDispatch();
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<Error | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      const configResponse = await apiClient.get(`${endpoints.APP.config}`);
      let domainConfig: any = false;
      const baseAppConfig = appStaticConf;
      const host = process.env.REACT_APP_HOST ?? window.location.host;

      // Check if API call was successful
      if (configResponse.data?.status == ApiStatus.success) {
        // API succeeded - merge API response with base config
        const apiConfig = configResponse.data.data;
        domainConfig = mergeDeep(baseAppConfig, apiConfig);
      } else {
        // API failed - determine domain-specific config and merge with base
        let fallbackConfig: any = null;

        if (
          host == 'b2b.sky360.co.il' ||
          host == 'stg-b2c.sky360.co.il' ||
          host == 'b2c.sky360.co.il'
        ) {
          fallbackConfig = appConfigSky360;
        } else if (
          host == 'stg-b2c.familybreak.co.il' ||
          host == 'www.familybreak.co.il'
        ) {
          fallbackConfig = appConfigFamily;
        } else if (host == 'issta.sky360.co.il') {
          fallbackConfig = appConfigIssta;
        } else if (host == 'sap.sky360.co.il') {
          fallbackConfig = appConfigSap;
        } else if (host == 'guestroom.sky360.co.il') {
          fallbackConfig = appConfigGuestRoom;
        } else if (host == 'ipc.sky360.co.il') {
          fallbackConfig = appConfigIpc;
        } else if (host == 'universal.sky360.co.il') {
          fallbackConfig = appConfigUniversal;
        } else if (host == 'www.skystudents.co.il') {
          fallbackConfig = appConfigStudents;
        } else if (host == 'soho.sky360.co.il') {
          fallbackConfig = appConfigSoho;
        } else if (host == 'hotel.sky360.co.il') {
          fallbackConfig = appConfigHotel360;
        }

        // Merge fallback config with base to ensure all defaults are present
        if (fallbackConfig) {
          domainConfig = mergeDeep(baseAppConfig, fallbackConfig);
        }
      }

      // leave this commented out for now, for testing purposes
      // DEV CODE - Always comment it before merging
      domainConfig = mergeDeep(baseAppConfig, appConfigSky360v2);

      if (!domainConfig) {
        console.error('Failed to load config based on domain - ' + host);
        return;
      }

      // Set document title
      document.title = domainConfig.siteConfig.name;

      // Dispatch merged config to Redux
      dispatch(updateConfig(domainConfig));

      // Set favicon
      if (domainConfig?.siteConfig?.favIcon) {
        setFavicon(domainConfig.siteConfig.favIcon);
      }

      // Initialize search state from URL params or config defaults
      const searchParams = new URLSearchParams(window.location.search);
      const fromValue = searchParams.get('from');

      dispatch(
        updateSearchInitialState({
          from: fromValue || domainConfig.siteConfig.defaultDate?.from,
          range: domainConfig.siteConfig.defaultDate?.range,
          destination: domainConfig.siteConfig.defaultDestination,
          destinationCity: domainConfig.siteConfig.defaultDestinationCity,
          departure: getDeparture(domainConfig.siteConfig.defaultDeparture),
          departureCity: domainConfig.siteConfig.defaultDepartureCity,
        })
      );

      // Configure theme
      ConfigProvider.config({
        theme: {
          primaryColor: domainConfig.theme.primary,
        },
      });

      setIsConfigLoaded(true);
    } catch (error) {
      console.error('App config failed to load:', error);
      setConfigError(
        error instanceof Error
          ? error
          : new Error('Failed to load app configuration')
      );
    }
  }, [dispatch]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return { isConfigLoaded, configError };
}
